import axios from "axios";
import api, { type ApiResponse } from "./api";
import type { PublisherDashboardApp } from "./publisherDashboard";

export type UnpublishApplicationResult = {
  applicationId: string;
  status: string;
  alreadyUnpublished: boolean;
};

export const UNPUBLISHABLE_APP_STATUSES = [
  "published",
  "suspended",
  "approved",
] as const;

export function unpublishEndpoint(applicationId: string): string {
  return `/publisher/apps/${applicationId}/unpublish`;
}

export function canOpenUnpublishConfirmation(
  app: Pick<PublisherDashboardApp, "status"> | null | undefined,
): boolean {
  if (!app) {
    return false;
  }

  if (app.status === "delisted") {
    return false;
  }

  return UNPUBLISHABLE_APP_STATUSES.includes(
    app.status as (typeof UNPUBLISHABLE_APP_STATUSES)[number],
  );
}

export function resolveUnpublishSuccessMessage(
  alreadyUnpublished: boolean,
): string {
  return alreadyUnpublished
    ? "This app is already unpublished."
    : "App unpublished. It will no longer appear in Discover.";
}

export function resolveUnpublishErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const status = error.response?.status;
    const message =
      typeof error.response?.data?.message === "string"
        ? error.response.data.message
        : null;

    if (status === 401) {
      return "Your session has expired. Please sign in again.";
    }

    if (status === 404) {
      return message ?? "Application not found.";
    }

    if (status === 400) {
      return (
        message ??
        "This application cannot be unpublished in its current state."
      );
    }

    if (message) {
      return message;
    }
  }

  return "Unable to unpublish the selected app. Please try again.";
}

export async function unpublishApplication(
  applicationId: string,
): Promise<UnpublishApplicationResult> {
  const response = await api.post<
    ApiResponse<{ application: UnpublishApplicationResult }>
  >(unpublishEndpoint(applicationId));

  return response.data.data.application;
}
